// v2 windmill — a smock mill: a stone ground storey under a tapered
// weatherboarded smock, a timber gallery deck ringing the waist (posts +
// handrail), an ogee-domed cap with a finial, four radial sails — timber spar
// with a cloth panel trailing it, long axis along the radius — and a little red
// fantail wheel on a tail pole at the back of the cap. Ported 1:1 from
// age-of-mcp's v2/windmill.ts, using the library's shared `foundationSlab` and
// `scaffold` helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, benchWork, spin, EFFECT_LOOP, type Part } from "../../lib";

const STONE  = "#8a8276";   // ground-storey masonry
const SMOCK  = "#e7ddc4";   // weatherboarded smock
const DECK   = "#7a5a34";   // gallery timber
const CAP    = "#5e4023";   // ogee cap
const SPAR   = "#6b4a26";   // sail spars + windshaft
const CLOTH  = "#f2ead6";   // sail cloth
const FAN    = "#b8432f";   // fantail blades
const DOOR   = "#4a3018";
const DARK   = "#39444e";   // window

const BASE   = 0.14;        // foundation slab top
const PLINTH = 0.22;        // stone storey height
const SMOCK_H = 0.62;       // smock height (smock top at 0.98)
const HUB_Y  = 1.0;         // sail hub height
const HUB_Z  = 0.30;        // sail hub forward of the tower

// The stone ground storey with the mill door on the front (+z).
function plinth(): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.34, 0.38, PLINTH, 8), color: STONE,
      position: [0, BASE + PLINTH / 2, 0] },
    { geometry: new THREE.BoxGeometry(0.18, 0.28, 0.05), color: DOOR,
      position: [0, BASE + 0.14, 0.36] },
  ];
}

// The tapered weatherboarded smock, full height or a partial build of `h`.
function smock(h: number): Part {
  const y0 = BASE + PLINTH;
  const t = h / SMOCK_H;
  return {
    geometry: new THREE.CylinderGeometry(0.33 - 0.11 * t, 0.33, h, 8),
    color: SMOCK, position: [0, y0 + h / 2, 0],
  };
}

// The gallery deck ringing the smock waist: platform disc, six posts and a
// thin handrail ring.
function gallery(): Part[] {
  const parts: Part[] = [
    { geometry: new THREE.CylinderGeometry(0.40, 0.40, 0.04, 8), color: DECK,
      position: [0, 0.52, 0] },
  ];
  for (let i = 0; i < 6; i++) {
    const a = (i * Math.PI) / 3;
    parts.push({
      geometry: new THREE.BoxGeometry(0.025, 0.14, 0.025), color: DECK,
      position: [Math.cos(a) * 0.37, 0.61, Math.sin(a) * 0.37],
    });
  }
  parts.push({
    geometry: new THREE.TorusGeometry(0.37, 0.014, 4, 8), color: DECK,
    position: [0, 0.68, 0], rotation: [Math.PI / 2, 0, 0],
  });
  return parts;
}

// The ogee-domed cap with its finial.
function cap(): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.26, 8, 6), color: CAP,
      position: [0, 1.04, 0], scale: [1, 0.7, 1] },
    { geometry: new THREE.ConeGeometry(0.045, 0.12, 6), color: SPAR,
      position: [0, 1.27, 0] },
  ];
}

// Four radial sails on the front hub: each a spar whose long axis lies along
// the radius (rotation ang - pi/2) carrying a cloth panel offset to its
// trailing side, exactly like a real common sail.
function sails(): Part[] {
  const parts: Part[] = [{
    geometry: new THREE.CylinderGeometry(0.05, 0.05, 0.12, 6), color: SPAR,
    position: [0, HUB_Y, HUB_Z], rotation: [Math.PI / 2, 0, 0], name: "sails",
  }];
  for (let i = 0; i < 4; i++) {
    const ang = (i * Math.PI) / 2 + Math.PI / 4;
    const cx = Math.cos(ang), sy = Math.sin(ang);
    const px = -Math.sin(ang), py = Math.cos(ang);   // trailing-side offset
    parts.push(
      { geometry: new THREE.BoxGeometry(0.035, 0.56, 0.025), color: SPAR,
        position: [cx * 0.28, HUB_Y + sy * 0.28, HUB_Z + 0.02],
        rotation: [0, 0, ang - Math.PI / 2], name: "sails" },
      { geometry: new THREE.BoxGeometry(0.10, 0.40, 0.015), color: CLOTH,
        position: [cx * 0.34 + px * 0.055, HUB_Y + sy * 0.34 + py * 0.055, HUB_Z],
        rotation: [0, 0, ang - Math.PI / 2], name: "sails" },
    );
  }
  return parts;
}

// The fantail at the back of the cap: a tail pole carrying a small radial
// wheel of red blades.
function fantail(): Part[] {
  const parts: Part[] = [
    { geometry: new THREE.BoxGeometry(0.025, 0.025, 0.26), color: CAP,
      position: [0, 1.0, -0.33], rotation: [-0.35, 0, 0] },
    { geometry: new THREE.CylinderGeometry(0.022, 0.022, 0.05, 6), color: SPAR,
      position: [0, 0.94, -0.47], rotation: [Math.PI / 2, 0, 0], name: "fantail" },
  ];
  for (let i = 0; i < 4; i++) {
    const a = (i * Math.PI) / 2 + Math.PI / 4;
    parts.push({
      geometry: new THREE.BoxGeometry(0.022, 0.13, 0.012), color: FAN,
      position: [Math.cos(a) * 0.065, 0.94 + Math.sin(a) * 0.065, -0.47],
      rotation: [0, 0, a - Math.PI / 2], name: "fantail",
    });
  }
  return parts;
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.86)];
  if (s === 1) return [foundationSlab(0.86), ...plinth(), smock(0.3), ...scaffold(0.7, 0.75)];
  if (s === 2) return [foundationSlab(0.86), ...plinth(), smock(SMOCK_H), ...gallery(), ...scaffold(0.7, 1.1)];
  return [
    foundationSlab(0.86),
    ...plinth(),
    smock(SMOCK_H),
    // A little window high on the smock front.
    { geometry: new THREE.BoxGeometry(0.08, 0.10, 0.03), color: DARK, position: [0, 0.78, 0.27] },
    ...gallery(),
    ...cap(),
    ...sails(),
    ...fantail(),
  ];
}

export const windmillV2 = defineModel({
  id: "windmill-v2",
  tiles: { w: 2, h: 2 },
  batchId: "fable",
  name: "Windmill (v2)",
  tags: ["building","windmill","low-poly","age-of-mcp","fable","animated"],
  summary: "Low-poly windmill.",
  description: "A sail-driven mill that grinds grain into flour.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [
    {
      // The mill at work: the sails turn about the front hub while the little
      // fantail wheel spins fast at the back of the cap.
      id: "active",
      label: "Sails turning",
      duration: EFFECT_LOOP * 2,
      variants: ["complete"],
      tracks: [
        spin("sails", [0, HUB_Y, HUB_Z], { duration: EFFECT_LOOP * 2, turns: 1 }),
        spin("fantail", [0, 0.94, -0.47], { duration: EFFECT_LOOP * 2, turns: 8 }),
      ],
    },
  ],
  stations: [
    { id: "bench", position: [0, 0.14, 0.44], facing: Math.PI },
  ],
  interactions: [
    { id: "work", label: "Working the mill", station: "bench", variants: ["complete"], ...benchWork() },
  ],
});
